import { cn } from "@/lib/utils";
import { UseMutationResult, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useRef } from "react";
import { useCategoryStore } from "@/stores/use-tutorial-progress";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { MixpanelActions } from "../../integrations/mixpanel/mixpanel";

export enum PreferenceState {
  Selected = "included",
  Excluded = "excluded",
  Default = "default",
}

export interface PreferenceItem {
  id: string;
  name: string;
  is_trial?: boolean;
}

interface PreferenceCloudProps<T extends PreferenceItem> {
  title: string;
  items: T[];
  isLoading: boolean;
  selectedItems?: string[];
  excludedItems?: string[];
  updatePreference: UseMutationResult<
    void,
    Error,
    { name: string; preferenceType: string | null }
  >;
  renderItemLabel?: (item: T) => string;
  currentCategory: string;
}

// Individual preference button component to handle click events properly
function PreferenceButton<T extends PreferenceItem>({
  item,
  itemState,
  getNextState,
  updatePreference,
  renderItemLabel,
  currentCategory,
}: {
  item: T;
  itemState: PreferenceState;
  getNextState: (
    currentState: PreferenceState,
    isDoubleClick: boolean
  ) => PreferenceState;
  updatePreference: UseMutationResult<
    void,
    Error,
    { name: string; preferenceType: string | null }
  >;
  renderItemLabel: (item: T) => string;
  currentCategory: string;
}) {
  const subscription = useSubscription();
  // Ref to store the click timer
  const clickTimerRef = useRef<number | null>(null);
  // Handle click with debounce to detect single vs double clicks
  const {
    getTutorialCountByPlaylist,
    getCompletedTutorialCountByPlaylist,
    setCurrentSelection,
    resetCurrentSelection,
  } = useCategoryStore((state) => state);

  const currentProgress = getCompletedTutorialCountByPlaylist(item.id);
  const totalTutorials = getTutorialCountByPlaylist(item.id);
  if (currentProgress == totalTutorials) return null;

  const handleClick = () => {
    if (clickTimerRef.current !== null) {
      // This is a double click - clear the timer and handle double click
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;

      // Handle double click action
      const nextState = getNextState(itemState, true);
      const preferenceType =
        nextState === PreferenceState.Selected
          ? "included"
          : nextState === PreferenceState.Excluded
          ? "excluded"
          : null;
      updatePreference.mutate({ name: item.id, preferenceType });
      if (preferenceType !== null) {
        setCurrentSelection(currentCategory, item.id);
        MixpanelActions.selectPlaylist(item);
      } else {
        resetCurrentSelection();
      }
    } else {
      // This might be a single click - set a timer to check
      clickTimerRef.current = window.setTimeout(() => {
        // If we reach here, it was a single click
        clickTimerRef.current = null;

        // Handle single click action
        const nextState = getNextState(itemState, false);
        const preferenceType =
          nextState === PreferenceState.Selected
            ? "included"
            : nextState === PreferenceState.Excluded
            ? "excluded"
            : null;
        updatePreference.mutate({ name: item.id, preferenceType });
        if (preferenceType !== null) {
          setCurrentSelection(currentCategory, item.id);
          MixpanelActions.selectPlaylist(item);
        } else {
          resetCurrentSelection();
        }
      }, 250); // 250ms delay to detect double clicks
    }
  };

  return (
    <button
      onClick={handleClick}
      className={cn(
        "group flex items-center gap-2 px-[0.7rem] py-[0.3rem] text-sm font-medium rounded-full transition-all duration-300",
        {
          "bg-primary  text-primary hover:bg-primary/20 dark:hover:bg-primary/80":
            itemState === PreferenceState.Selected,
          "bg-destructive/10 text-destructive hover:bg-destructive/20 line-through":
            itemState === PreferenceState.Excluded,
          "bg-[#FFFFFF] border border-gray-600/10 text-secondary-foreground dark:bg-[#FFFFFF] dark:text-white hover:bg-secondary/90":
            itemState === PreferenceState.Default,
        }
      )}
    >
      <div
        className={cn(
          "transition-transform group-hover:scale-105 whitespace-nowrap dark:text-black max-w-full text-wrap text-left",
          itemState === PreferenceState.Selected && "text-white dark:text-white"
        )}
      >
        {renderItemLabel(item)}
      </div>
      <span
        className={cn(
          "text-gray-400 text-xs ml-auto bg-gray-500/[0.05] py-1 px-2.5 rounded-full dark:text-gray-700 text-nowrap",
          itemState === PreferenceState.Selected && "text-white dark:text-white"
        )}
      >
        {currentProgress || 0} / {totalTutorials || 0}
      </span>
    </button>
  );
}

export function PreferenceCloud<T extends PreferenceItem>({
  title,
  items,
  isLoading,
  selectedItems = [],
  excludedItems = [],
  updatePreference,
  currentCategory,
  renderItemLabel = (item) => item.name,
}: PreferenceCloudProps<T>) {
  const { t } = useTranslation();

  const getItemState = (name: string): PreferenceState => {
    if (selectedItems.includes(name)) return PreferenceState.Selected;
    if (excludedItems.includes(name)) return PreferenceState.Excluded;
    return PreferenceState.Default;
  };

  const getNextState = (
    currentState: PreferenceState,
    isDoubleClick: boolean
  ): PreferenceState => {
    if (isDoubleClick) {
      return currentState === PreferenceState.Excluded
        ? PreferenceState.Default
        : PreferenceState.Excluded;
    }
    if (currentState === PreferenceState.Excluded) {
      return PreferenceState.Default;
    }
    return currentState === PreferenceState.Selected
      ? PreferenceState.Default
      : PreferenceState.Selected;
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-6 transition-all duration-300">
        <h2 className="text-xl font-semibold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent text-center md:text-left">
          {title}
        </h2>
        <div className="flex flex-wrap gap-2 justify-center md:justify-start">
          {[1, 2, 3].map((n) => (
            <div
              key={n}
              className="h-8 w-20 animate-pulse rounded-full bg-muted"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="py-3 lg:px-3 space-y-3 transition-all duration-300 max-w-full">
      <div className="flex flex-wrap gap-y-2 gap-x-1 justify-start px-2">
        {items.map((item) => {
          const itemState = getItemState(String(item.id));
          return (
            <PreferenceButton
              currentCategory={currentCategory}
              key={item.id}
              item={item}
              itemState={itemState}
              getNextState={getNextState}
              updatePreference={updatePreference}
              renderItemLabel={renderItemLabel}
            />
          );
        })}
      </div>
    </div>
  );
}
